import React, { useEffect, useState } from "react";
import { IconCirclePlus, IconX, IconUpload, IconBellRinging } from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Logo from "../../assets/images/paqtory.png";
import {
  clearSingleAdmin,
  fetchAdminDetails,
  updateAdmin,
  registerAdmin,
} from "../../redux/slices/adminsSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

// Sidebar Navigation Item Component
const NavItem = ({ label, to, active }) => (
  <a
    href={to}
    className={`px-3 py-2 rounded-md cursor-pointer flex items-center gap-2 ${active ? "bg-white/20" : "hover:bg-white/10"
      }`}
  >
    <span>{label}</span>
  </a>
);

const AdminAccountForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setLayoutConfig } = useAdminLayout();

  const dispatch = useDispatch();
  const { currentAdmin: singleAdmin, loading, error } = useSelector((state) => state.admins);
  const isNewAdmin = id === "new";
  const [selectedPermission, setSelectedPermissionList] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    role: "Editor",
    profile_picture: null,
    permissions: []
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [activeNav, setActiveNav] = useState("Admin Account & Roles");
  const [selectedRole, setSelectedRole] = useState(null);

  // Fetch admin data when editing
  useEffect(() => {
    if (!isNewAdmin && id) {
      dispatch(fetchAdminDetails(id));
    }

    setLayoutConfig({
      title: `${isNewAdmin ? "Admin Accounts and Role / Add" : "Admin Accounts and Role / Edit"}`,
      showBackButton: true,
      scrollable: true,
    });
    return () => {
      dispatch(clearSingleAdmin());
    };
  }, [dispatch, id, isNewAdmin, setLayoutConfig]);

  // Populate form when data is loaded
  useEffect(() => {
    if (singleAdmin && !isNewAdmin) {
      // Convert permissions to frontend format

      // Set the role from backend and select it in the table
      const backendRole = singleAdmin.role || "Editor";
      const displayRole = backendRole === "super_admin" ? "SuperAdmin" :
        backendRole === "manager" ? "Manager" : "Editor";

      setSelectedRole(displayRole);

      const permissionsArray = [];
      if (singleAdmin.custom_permissions) {
        singleAdmin.custom_permissions.forEach(perm => {
          // Map backend permission names to frontend format
          let frontendPerm = perm;
          if (perm === "manage_users") frontendPerm = "manageUsers";
          if (perm === "edit_content") frontendPerm = "editClient";
          if (perm === "view_reports") frontendPerm = "viewReports";

          permissionsArray.push(`${displayRole}:${frontendPerm}`);
        });
      }
      // selectedPermission = ;
      setSelectedPermissionList(permissionsArray);

      setFormData({
        full_name: singleAdmin.full_name || "",
        email: singleAdmin.email || "",
        role: singleAdmin.role || "Editor",
        profile_picture: null,
        permissions: permissionsArray
      });



      // Set profile picture preview if available
      if (singleAdmin.profile_picture) {
        setPreviewImage(singleAdmin.profile_picture);
      }
    }
  }, [singleAdmin, isNewAdmin]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle row selection
  const handleRowClick = (role) => {
    if (selectedRole === role) {
      setSelectedRole(null);
      setFormData(prev => ({ ...prev, role: "" }));
    } else {
      setSelectedRole(role);
      setFormData(prev => ({ ...prev, role }));
    }
  };

  const handlePermissionChange = (permission, e) => {
    e.stopPropagation();
    const permissionKey = `${selectedRole}:${permission}`;

    setFormData(prev => {
      const newPermissions = prev.permissions.includes(permissionKey)
        ? prev.permissions.filter(p => p !== permissionKey)
        : [...prev.permissions, permissionKey];

      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, profile_picture: file }));

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {

      const formDataToSend = new FormData();
      formDataToSend.append("full_name", formData.full_name);
      formDataToSend.append("email", formData.email);

      const currentRole = formData.role || 'editor';
      if (currentRole === 'Editor') {
        formDataToSend.append("role", "editor");
        formDataToSend.append("user_account_type", "editor");
      } else if (currentRole === 'Manager') {
        formDataToSend.append("role", "manager");
        formDataToSend.append("user_account_type", "manager");
      } else if (currentRole === 'SuperAdmin') {
        formDataToSend.append("role", "super_admin");
        formDataToSend.append("user_account_type", "super_admin");
      }

      // Convert permissions to backend format
      const permissionTemplate = formData.permissions.map((permission) => {
        const [role, perm] = permission.split(":");
        return `${perm}`;
      });

      const finalPermissionList = permissionTemplate.map((permission) => {
        if (permission === "manageUsers") return "manage_users";
        if (permission === "editClient") return "edit_content";
        if (permission === "viewReports") return "view_reports";
        return permission;
      }).filter(perm => perm); // Remove any undefined values

      formDataToSend.append("permissions", JSON.stringify(finalPermissionList));

      if (formData.profile_picture) {
        formDataToSend.append("profile_picture", formData.profile_picture);
      }



      if (isNewAdmin) {
       
        const result = await dispatch(registerAdmin(formDataToSend)).unwrap();
        
      } else {
        formDataToSend.append("admin_id", id);
        const result = await dispatch(
          updateAdmin(formDataToSend)
        ).unwrap();
   
      }
    } catch (error) {
      console.error("Error submitting form:", error);
   
    }
  };

  // Helper function to check if a permission is enabled
  const isPermissionEnabled = (role, permission) => {
    selectedPermission.forEach((perm) => {
      var ch = `${role}:${permission}`;
      if (perm === ch) {
        return true;
      }
    });

    return formData.permissions.includes(`${role}:${permission}`);
  };

  if(loading) {
    return <LoadingFallback />
  }
  if(error){
    return <ErrorFallback onRetry={() => dispatch(fetchAdminDetails(id))} />
  }

  return (
    <div>
      <main className="flex-1 overflow-y-auto px-2 py-2">
        {error && (
          <div className="mb-4 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">
            <p>{error}</p>
          </div>
        )}

        <div className="border-2  rounded-md bg-[#FFFFFF] p-4">
          <form onSubmit={handleSubmit}>
            {/* PROFILE SECTION */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#0B0C52] mb-4 pb-2 text-center">
                Profile
              </h3>
              <div className="flex flex-col items-center gap-6">
                {/* Profile Picture Upload */}
                <div className="flex flex-col items-center">
                  <div className="relative w-32 h-32 mb-4">
                    {previewImage ? (
                      <img
                        src={previewImage}
                        alt="Profile Preview"
                        className="w-full h-full rounded-full object-cover border-2 border-[#463BC9]"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 border-2 border-[#463BC9] flex items-center justify-center">
                        <span className="text-gray-500">No Image</span>
                      </div>
                    )}
                  </div>
                  <label
                    className={`cursor-pointer bg-[#0B0C52] text-white px-4 py-2 rounded flex items-center gap-2 ${loading ? "opacity-70" : "hover:opacity-90"
                      }`}
                  >
                    <IconUpload size={16} />
                    Upload
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={loading}
                    />
                  </label>
                </div>

                {/* Admin Info */}
                <div className="w-full">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#0B0C52] mb-1">
                        Admin Name
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        placeholder="Enter Admin Name"
                        className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        required
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#0B0C52] mb-1">
                        Admin Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter Admin Email"
                        className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* if its editing display the readonly fields of registration_date and last long  */}
            {!isNewAdmin && (
              <div className="mb-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0B0C52] mb-1">
                      Registration Date
                    </label>
                    <input

                      type="text"
                      name="registration_date"
                      value={singleAdmin?.registration_date || ""}
                      placeholder="Registration Date"
                      className="border-2 border-gray-300 bg-gray-100 text-gray-500 w-full rounded px-3 py-2 focus:outline-none cursor-not-allowed"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0B0C52] mb-1">
                      Last Login
                    </label>
                    <input

                      type="text"
                      name="last_login"

                      value={singleAdmin?.last_login || ""}
                      placeholder="Last Login"
                      className="border-2 border-gray-300 bg-gray-100 text-gray-500 w-full rounded px-3 py-2 focus:outline-none cursor-not-allowed"

                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}
            {/* PERMISSIONS TABLE */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-[#0B0C52] mb-4 border-b-2 border-[#463BC9] pb-2">
                Permissions
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-[#463BC9]">
                  <thead className="bg-[#0B0C52] text-white">
                    <tr>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-center">Manage Users</th>
                      <th className="px-4 py-2 text-center">Edit Client</th>
                      <th className="px-4 py-2 text-center">View Reports</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['SuperAdmin', 'Editor', 'Manager'].map((role) => (
                      <tr
                        key={role}
                        onClick={() => handleRowClick(role)}
                        className={`border-b border-[#463BC9] cursor-pointer ${selectedRole === role ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                      >
                        <td className="px-4 py-2 font-medium">{role}</td>
                        {['manageUsers', 'editClient', 'viewReports'].map(permission => (
                          <td

                            key={permission}
                            className="px-4 py-2 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isPermissionEnabled(role, permission)}
                              onChange={(e) => handlePermissionChange(permission, e)}
                              disabled={loading || (selectedRole !== role)}
                              className="h-4 w-4 text-[#463BC9] focus:ring-[#463BC9]"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>

            {/* FORM ACTIONS */}
            <div className="flex justify-end gap-4 pt-4 border-t border-[#463BC9]">
              <button
                type="button"
                onClick={() => navigate("/admin/roles")}
                disabled={loading}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded flex items-center gap-1 hover:opacity-80 disabled:opacity-70"
              >
                <IconX size={18} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#0B0C52] text-white px-4 py-2 rounded flex items-center gap-1 hover:opacity-90 disabled:opacity-70"
              >
                {loading ? (
                  <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  <IconCirclePlus size={18} />
                )}
                {isNewAdmin ? "Add Admin" : "Update Admin"}
              </button>
            </div>
          </form>
        </div>
      </main>

    </div>

  );
};

export default AdminAccountForm;